import React, { ReactChild } from 'react';
import { Plus16, Minus16 } from '@honeybook/icons';
import {
    SummaryContainer,
    IconContainer,
    SummaryContent
} from './HBAccordion.styles';
import { vars } from '../../theme';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';

export interface HBAccordionSummaryProps {
    /** Content rendered in the header row, typically the item's label text or a custom header layout. */
    children: ReactChild | ReactChild[];
    /** Injected by HBAccordion via cloneElement; do not set directly. */
    toggle?: (id: string) => void;
    /** Injected by HBAccordion via cloneElement; do not set directly. */
    isExpanded?: boolean;
    /** Custom icon shown when this item is expanded, replacing the default Minus icon. */
    collapseIcon?: ReactChild;
    /** Custom icon shown when this item is collapsed, replacing the default Plus icon. */
    expandedIcon?: ReactChild;
}

const HBAccordionSummary = ({
    id,
    testIdName,
    testId,
    children,
    toggle,
    isExpanded = false,
    collapseIcon,
    expandedIcon
}: HBAccordionSummaryProps & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const handleClick = () => toggle(isExpanded ? '' : id);

    const renderIcon = () => {
        if (isExpanded) {
            return (
                collapseIcon || <Minus16 color={vars.colors.text.secondary} />
            );
        }

        return expandedIcon || <Plus16 color={vars.colors.text.secondary} />;
    };

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'accordion-summary',
        componentName: 'HBAccordionSummary'
    });

    return (
        <SummaryContainer id={id} onClick={handleClick} testId={finalTestId}>
            <SummaryContent>{children}</SummaryContent>
            <IconContainer>{renderIcon()}</IconContainer>
        </SummaryContainer>
    );
};

export default HBAccordionSummary;
